import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import { useAbility } from '@casl/react'
import { Button as AntdButton, Button, Dropdown, Layout, Menu } from 'antd'
import { ItemType, MenuItemGroupType } from 'antd/lib/menu/hooks/useItems'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useLocation, useNavigate } from 'react-router'
import CurvoLogoVertical from 'src/curvo-logo-vertical.png'
import styled from 'styled-components'
import { CustomerTypeEnum, MeContext, UserPermissionFeature, useRemoveUserCloak } from '../../api'
// @ts-ignore
import Env from '../../api/gen/Env'
import { AbilityContext } from '../../api/components/Can'
import { useLogout } from '../../common/hooks/useLogout'
import { isCloakedUser } from '../../api/utils/cloak'
import { hasSupplierPortalAccess } from '../../api/utils/supplierPortalAccess'
import { isNonAdminSupplier } from '../../api/utils/nonAdminSupplier'
import { canEmptyCatalog, useEmptyCatalogGateQuery } from '../../api/hooks/emptyCatalog'
import { LaunchDarklyFeatures } from '../../common/constants'
import {
  BATCH_RESULTS_PATH,
  CATALOG_PATH,
  CROSSES_PATH,
  EMPTY_CATALOG_PATH,
  SUPPLIER_PORTAL_CONFIGURATIONS_KEY,
  SUPPLIER_PORTAL_PATH,
  TIERS_PATH,
} from '../SupplierPortal/paths'
import { HelpMenu } from './HelpMenu'
import { UserHeaderSection } from './UserHeaderSection'

const navSections: NavigationSectionItemProps[] = [
  {
    group: 'At a Glance',
    subMenus: [
      {
        name: 'Market Baskets',
        path: '/analytics/baskets',
        ability: [
          UserPermissionFeature.Baskets,
          UserPermissionFeature.BasketsReadonly,
          UserPermissionFeature.RebateOptimizer,
        ],
      },
      {
        name: 'Contracts',
        path: '/analytics/contracts',
        ability: [UserPermissionFeature.Contracts, UserPermissionFeature.RebateOptimizer],
      },
      {
        name: 'Rebate Optimizer',
        path: '/analytics/rebate-management',
        ability: [UserPermissionFeature.RebateOptimizer],
      },
      {
        name: 'RFx',
        path: '/strategic-sourcing/opportunities',
        ability: [UserPermissionFeature.Opportunities],
      },
      {
        name: 'Collaboration',
        path: '/aperture/collaboration',
        ability: [UserPermissionFeature.ProjectAperture],
      },
      // BroadJump/Playbook lives in a separate app. Use its env-configured URL (plus the fixed
      // PlaybookV2 path) as the path so the menu's onClick treats it as an external link.
      // Omitted entirely when no URL is configured.
      ...(Env.broadjumpBaseUrl
        ? [
            {
              name: 'Initiatives',
              path: `${Env.broadjumpBaseUrl.replace(/\/+$/, '')}/PlaybookV2/PlaybookV2`,
              ability: [UserPermissionFeature.Initiatives],
            },
          ]
        : []),
    ],
  },
  {
    group: 'In Depth',
    subMenus: [
      {
        name: 'Data Explorer',
        path: '/analytics/spending',
        ability: [UserPermissionFeature.Spending, UserPermissionFeature.RebateOptimizer],
      },
      {
        name: 'Reports',
        path: '/analytics/reports/console',
        ability: [UserPermissionFeature.Reports],
      },
      {
        name: 'Sourcing Plans',
        path: '/strategic-sourcing/sourcing-plans',
        ability: [UserPermissionFeature.SourcingPlans],
      },
      {
        name: 'Project Tracker',
        path: '/strategic-sourcing/project-tracker',
        ability: [UserPermissionFeature.ProjectTracker],
      },
      {
        name: 'CX Project Tracker',
        path: '/strategic-sourcing/cx-project-tracker',
        ability: [UserPermissionFeature.CxProjectTracker],
      },
    ],
  },
  {
    group: 'Enrich',
    subMenus: [
      {
        name: 'My Queue',
        path: '/enrich',
        ability: [UserPermissionFeature.Enrich],
      },
      {
        name: 'Team Queue',
        path: '/enrich/team-queue',
        ability: [UserPermissionFeature.TeamQueue],
      },
      {
        name: 'Manage Catalog',
        path: '/enrich/catalog',
        ability: [UserPermissionFeature.Catalog],
      },
      {
        name: 'Import Study',
        path: '/enrich/import-study',
        ability: [UserPermissionFeature.ImportStudy],
      },
    ],
  },
  {
    group: 'Supplier Portal',
    subMenus: [
      { name: 'Opportunities', path: SUPPLIER_PORTAL_PATH },
      {
        name: 'Configurations',
        path: SUPPLIER_PORTAL_CONFIGURATIONS_KEY,
        children: [
          { name: 'Catalog', path: CATALOG_PATH },
          { name: 'Crosses', path: CROSSES_PATH },
          { name: 'Tiers', path: TIERS_PATH },
          { name: 'Batch Results', path: BATCH_RESULTS_PATH },
          { name: 'Empty Catalog', path: EMPTY_CATALOG_PATH },
        ],
      },
    ],
  },
]

const adminNavSections: NavigationSectionItemProps[] = [
  {
    group: 'Project Aperture',
    subMenus: [
      {
        name: 'PO Spend',
        path: '/project-aperture/spend',
      },
      {
        name: 'Case Spend',
        path: '/project-aperture/cases',
      },
      {
        name: 'Enloe filtered for ZB',
        path: '/project-aperture/enloe-zb',
      },
      {
        name: 'Trinity filtered for ZB',
        path: '/project-aperture/trinity-zb',
      },
    ],
  },
  {
    group: 'Analytics',
    subMenus: [
      {
        name: 'Reports',
        path: '/analytics/reports/console',
      },
    ],
  },
  {
    group: 'Manage',
    subMenus: [
      {
        name: 'Customers',
        path: '/manage/customer',
      },
      {
        name: 'Users',
        path: '/manage/users',
      },
      {
        name: 'Featured Contents',
        path: '/manage/homepage/featured-contents',
      },
    ],
  },
]

const deriveOpenKeys = (pathname: string): string[] =>
  pathname.startsWith(SUPPLIER_PORTAL_CONFIGURATIONS_KEY) ? [SUPPLIER_PORTAL_CONFIGURATIONS_KEY] : []

type MainSideBarProps = {}

export const MainSideBar: React.FC<MainSideBarProps> = () => {
  const location = useLocation()
  const navigate = useNavigate()

  const { data } = useContext(MeContext)
  const ability = useAbility(AbilityContext)
  const [removeCloak] = useRemoveUserCloak()

  const [collapsed, setCollapsed] = useState(false)
  const [activeItem, setActiveItem] = useState<string>()
  const [openKeys, setOpenKeys] = useState<string[]>(() => deriveOpenKeys(location.pathname))

  const user = data && data.me && data.me.user
  const isAdmin = data && data.me && data.me.isAdmin
  const isPowerUser = (data?.me.powerUserList || []).filter(org => org.isPowerUser).length > 0
  const gate = useEmptyCatalogGateQuery(data?.me)

  const menuLogout = useLogout()

  const handleExitCloaking = async () => {
    await removeCloak()
    window.location.replace('/')
  }

  const featureFlags = useFlags()
  const supplierPortalEnabled = featureFlags[LaunchDarklyFeatures.supplierPortal]
  const isSupplier = data?.me.user.customer.type === CustomerTypeEnum.Supplier

  const filteredNavigationItems = useMemo(() => {
    if (isAdmin) {
      return adminNavSections
    }

    return navSections
      .map(n => ({
        ...n,
        subMenus: n.subMenus
          ?.filter(s => !s.ability || s.ability.some(ab => ability.can('access', ab)))
          // Remove ProjectTracker menu item when cloaking due to unable to authorize with Appian using SSO
          .filter(s => !user?.cloakUserId || !s.ability?.includes(UserPermissionFeature.ProjectTracker))
          // #7806 — the same shared predicate as the route guard and the provider: a cloaked
          // supplier-typed principal keeps the portal group with the flag OFF (AC-O1).
          .filter(
            s => !s.path.startsWith(SUPPLIER_PORTAL_PATH) || hasSupplierPortalAccess(data?.me, supplierPortalEnabled),
          )
          // #7358 (D1): the Empty Catalog child is gated on the SAME exported predicate as its page. The
          // gate document is skip-fired inside the hook — it is never issued for a principal who is not
          // customer-cloaked — so every principal but a customer-cloaked admin issues zero new requests.
          .map(s =>
            s.children
              ? {
                  ...s,
                  children: s.children.filter(
                    c => c.path !== EMPTY_CATALOG_PATH || canEmptyCatalog(data?.me, gate.data),
                  ),
                }
              : s,
          )
          // #7701: a supplier sees ONLY portal-pathed items — by rule, whatever their abilities grant.
          // A no-op for everyone else (`!isSupplier` short-circuits). Sits BELOW the isAdmin early
          // return, so an admin — supplier-typed or not — never evaluates it.
          .filter(s => !isSupplier || s.path.startsWith(SUPPLIER_PORTAL_PATH)),
      }))
      .filter(n => n.subMenus?.length)
  }, [ability, user?.cloakUserId, isAdmin, supplierPortalEnabled, isSupplier, data?.me, gate.data])

  // #7907: a supplier's nav has no Home item — `/` resolves to Opportunities for them (HomeRoute), so the
  // route and the nav agree. The same population as the #7701 rule above (`isNonAdminSupplier`, flag-free,
  // a supplier-cloaked admin included); everyone else keeps today's item, first, unchanged.
  const mainMenuItems: ItemType[] = useMemo(
    () => [
      ...(isNonAdminSupplier(data?.me) ? [] : [{ label: 'Home', key: '/' }]),
      ...filteredNavigationItems.map(i => ({
        type: 'group',
        label: i.group,
        key: i.group,
        children: i.subMenus?.map(si =>
          si.children
            ? { label: si.name, key: si.path, children: si.children.map(c => ({ label: c.name, key: c.path })) }
            : { label: si.name, key: si.path },
        ),
      })),
    ],
    [filteredNavigationItems, data?.me],
  )

  useEffect(() => {
    if (location.pathname === '/') {
      setActiveItem('/')
      return
    }

    // Supplier Portal branch — the retired SupplierPortalNav's semantics, migrated verbatim
    // (discovery §3.4): startsWith cascade over the three child paths (so tier-detail paths keep
    // selecting Tiers), Opportunities as the fallback for the index and any unmatched portal path.
    if (location.pathname.startsWith(SUPPLIER_PORTAL_PATH)) {
      setActiveItem(
        location.pathname.startsWith(CATALOG_PATH)
          ? CATALOG_PATH
          : location.pathname.startsWith(CROSSES_PATH)
          ? CROSSES_PATH
          : location.pathname.startsWith(TIERS_PATH)
          ? TIERS_PATH
          : // #7338: appended AFTER the Tiers branch so tier-detail paths keep selecting Tiers. Its
          // path prefixes none of the three above and none of them prefixes it, so no branch is shadowed.
          location.pathname.startsWith(BATCH_RESULTS_PATH)
          ? BATCH_RESULTS_PATH
          : // #7358: appended AFTER the Batch Results branch. `…/configurations/empty-catalog` is not a
          // prefix of catalog|crosses|tiers|batch-results and none of them is a prefix of it (the first
          // character after `configurations/` differs from `catalog`'s), so no branch is shadowed in
          // either direction. No segment-boundary guard — #7618's deliberate posture.
          location.pathname.startsWith(EMPTY_CATALOG_PATH)
          ? EMPTY_CATALOG_PATH
          : SUPPLIER_PORTAL_PATH,
      )
      return
    }

    const matchedMenu = mainMenuItems
      .flatMap(i => (i as MenuItemGroupType).children || [])
      .find(sub => sub?.key && location.pathname.includes(sub.key as string))

    setActiveItem((matchedMenu?.key as string) || undefined)
  }, [mainMenuItems, location.pathname])

  useEffect(() => {
    setOpenKeys(deriveOpenKeys(location.pathname))
  }, [location.pathname])

  return (
    <StyledSider trigger={null} collapsed={collapsed} collapsible collapsedWidth={24} width={165}>
      <LogoSection collapsed={collapsed}>
        <div style={{ alignSelf: 'end' }}>
          <Button
            icon={collapsed ? <RightOutlined /> : <LeftOutlined />}
            type="text"
            style={{ color: 'var(--color-neutral-0)' }}
            onClick={() => setCollapsed(c => !c)}
          />
        </div>
        <a href="/" hidden={collapsed}>
          <img src={CurvoLogoVertical} alt="Curvo" width={100} />
        </a>
      </LogoSection>
      <MenuWrapper hidden={collapsed}>
        <div>
          <MainMenu
            mode="inline"
            items={mainMenuItems}
            selectedKeys={activeItem ? [activeItem] : []}
            openKeys={openKeys}
            onOpenChange={setOpenKeys}
            onClick={({ key }) => {
              if ((key as string).startsWith('http')) {
                window.open(key as string, '_blank', 'noopener,noreferrer')
              } else {
                navigate(key as string)
              }
            }}
          />
          <BottomMenu>
            {user && (isAdmin || isCloakedUser(user)) ? (
              <UserHeaderSection hideAvatar user={user} style={{ padding: '0 24px' }} />
            ) : null}
            <Dropdown
              placement="bottomRight"
              destroyPopupOnHide={true} // when menu is reopened, prevent focus from being on last focused item when keyboard navigating
              overlay={<HelpMenu />}
              trigger={['click']}>
              <HelpButton>Help &amp; Support</HelpButton>
            </Dropdown>
            {user && (
              <Button
                type="text"
                className={location.pathname.includes(`/users/${user.id}`) ? 'item-selected' : ''}
                onClick={() => navigate(`/users/${user.id}`)}>
                Profile
              </Button>
            )}
            {isAdmin ? (
              <Button
                type="text"
                className={location.pathname.includes('/manage/customer') ? 'item-selected' : ''}
                onClick={() => navigate('/manage/customer')}>
                Customers
              </Button>
            ) : (
              <Button
                type="text"
                className={location.pathname.includes(`/manage/customer/${user?.customer.id}`) ? 'item-selected' : ''}
                onClick={() => navigate(`/manage/customer/${user?.customer.id}`)}>
                Settings
              </Button>
            )}
            {isPowerUser && (
              <Button
                type="text"
                className={location.pathname.includes('/manage/users') ? 'item-selected' : ''}
                onClick={() => navigate('/manage/users')}>
                Users
              </Button>
            )}
            {/* #7806 — the ONE mounted exit control, now keyed on either cloak marker. Same button,
                same handler. R4: a cloaked admin must always have a way out from the UI. */}
            {isCloakedUser(user) && (
              <Button type="text" onClick={handleExitCloaking}>
                Exit Cloaking
              </Button>
            )}
            <Button type="text" onClick={menuLogout}>
              Log Out
            </Button>
          </BottomMenu>
        </div>
      </MenuWrapper>
    </StyledSider>
  )
}

type NavigationSectionItemProps = {
  group: string
  subMenus?: NavigationSubMenuItemProps[]
}

type NavigationSubMenuItemProps = {
  name: string
  path: string // leaf: the route path (= menu key); parent: the path prefix used as the submenu key
  ability?: UserPermissionFeature[]
  children?: { name: string; path: string }[] // presence ⇒ renders as an inline collapsible submenu
}

const StyledSider = styled(Layout.Sider)`
  display: flex;
  flex-direction: column;
  background: var(--color-blue-70);

  > .ant-layout-sider-children {
    display: flex;
    flex-direction: column;
    padding: 0 0 24px 0px;
    gap: 24px;
  }
`

const MenuWrapper = styled.div`
  display: flex;
  overflow: hidden auto;
  flex-grow: 1;

  > div {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    flex: 1 0 0;
    gap: 48px;
  }
`

const LogoSection = styled.div<{ collapsed: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  gap: 16px;
  flex-direction: column;

  button {
    margin-right: ${props => (props.collapsed ? '-4px' : '0')};
  }
`

const MainMenu = styled(Menu)`
  width: 100%;
  background-color: transparent;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  border: none;

  li,
  ul {
    width: 100%;
  }

  .ant-menu-item {
    padding: 4px 24px;
    align-items: center;
    color: #ffffff;
    height: auto;
    line-height: 1.5em;
    margin: 0 !important;
  }

  .ant-menu-item-group-title {
    padding: 4px 24px;
    height: auto;
    line-height: 1.2em;
    color: var(--color-neutral-60);
  }

  &.ant-menu:not(.ant-menu-horizontal) .ant-menu-item-selected {
    background-color: #00acff;
  }

  .ant-menu-item-active {
    color: #ffffff !important;
  }

  .ant-menu-submenu-title {
    padding: 4px 34px 4px 24px;
    color: #ffffff;
    height: auto;
    line-height: 1.5em;
    margin: 0 !important;
  }

  /* antd tints title text/arrow blue on hover/open/selected (menu less :78-85,:335-337);
     the sidebar carries no color-based parent affordance — same counter-idiom as :422-424 */
  .ant-menu-submenu-title:hover {
    color: #ffffff !important;
  }
  .ant-menu-submenu-arrow {
    color: #ffffff !important;
  }

  .ant-menu-sub.ant-menu-inline {
    background: transparent;
  }

  .ant-menu-sub.ant-menu-inline > .ant-menu-item {
    height: auto;
    line-height: 1.5em;
    list-style-type: none;
  }

  &.ant-menu:not(.ant-menu-horizontal) .ant-menu-item::after {
    display: none;
  }

  &.ant-menu:not(.ant-menu-horizontal) .ant-menu-item,
  &.ant-menu:not(.ant-menu-horizontal) .ant-menu-submenu-title {
    width: 100%;
  }
`

const BottomMenu = styled.div`
  display: flex;
  gap: 8px;
  flex-direction: column;
  padding: 0;
  width: 100%;

  .ant-dropdown-trigger {
    margin: 0 24px;
  }

  button:not(.ant-dropdown-trigger) {
    padding: 0 10px 0 24px;
    justify-content: start;
    color: #ffffff;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
    height: auto;
    width: 100%;
  }

  button:hover {
    color: #ffffff;
  }

  button.item-selected:not(.ant-dropdown-trigger) {
    color: #ffffff !important;
    background-color: var(--color-blue-50);
  }
`

const HelpButton = styled(AntdButton)`
  background-color: transparent;
  color: ${props => props.theme.ColorNeutral40};
  border: 2px solid ${props => props.theme.ColorNeutral60};

  padding: 5px 8px;
  display: flex;
  gap: 8px;

  :hover {
    background-color: #00acff;
    color: ${props => props.theme.ColorNeutral0};
    border: 2px solid ${props => props.theme.ColorNeutral80};
  }
  :focus-visible {
    background-color: #00acff;
    color: ${props => props.theme.ColorNeutral0};
    border: 2px solid ${props => props.theme.ColorBlue50} !important;
  }
  :focus {
    background-color: #00acff;
    color: ${props => props.theme.ColorNeutral0};
    border: 2px solid ${props => props.theme.ColorNeutral80};
  }
`
